// Spec 082 — o contrato da importação de arquivo, reescrito para uma importação que ESCREVE.
//
// O mapeamento deixa de ser `cabeçalho → campo canônico` e passa a ser `cabeçalho → decisão`. A
// decisão é ignorar a coluna ou apontá-la para uma LISTA de destinos, cada destino sendo a tripla
// linha + produto + sentido. A lista é o que permite a mesma coluna ser saída de uma linha e
// consumo da seguinte — a cadeia entre linhas aparece no relatório sem virar vínculo estrutural.
//
// É aqui, e só aqui, que o vocabulário da fábrica de origem existe. "NTC" é um equipamento,
// "Cozinha" é um setor, "Granel" é uma forma de comercialização: do ponto de vista da linha, os
// três são a mesma coisa — saída medida, de um produto, num sentido.
import { z } from "zod";
import { LossTypeSchema, UuidSchema } from "./common.js";
import { ProductionRoleSchema } from "./parameters-counter.js";
import {
  findDuplicateCharacteristic,
  ImportCharacteristicColumnSchema,
  ImportOriginPreviewSchema,
  ImportOriginRuleSchema,
  ImportOriginRulesSchema,
  ValueTranslationSchema,
} from "./data-import-characteristics.js";

export * from "./data-import-characteristics.js";

// ── Destino de coluna ────────────────────────────────────────────────────────────────────────

export const ImportColumnTargetSchema = z
  .object({
    lineId: UuidSchema,
    productId: UuidSchema,
    role: ProductionRoleSchema,
    lossType: LossTypeSchema.optional(),
  })
  .strict()
  .refine((t) => (t.role === "SCRAPS") === (t.lossType !== undefined), {
    message: "Informe o tipo de perda quando o sentido for Refuga, e só nesse caso.",
    path: ["lossType"],
  });
export type ImportColumnTarget = z.infer<typeof ImportColumnTargetSchema>;

export const ImportColumnMappingSchema = z.discriminatedUnion("action", [
  z.object({ action: z.literal("IGNORE") }).strict(),
  z
    .object({
      action: z.literal("MAP"),
      targets: z.array(ImportColumnTargetSchema).min(1),
    })
    .strict(),
  // Spec 084 — a coluna descreve a BATELADA, não uma quantidade. Ou uma coisa ou outra: uma coluna
  // que fosse as duas somaria um lote no total produzido.
  z
    .object({
      action: z.literal("CHARACTERISTIC"),
      characteristicId: UuidSchema,
      translations: z.record(z.string(), ValueTranslationSchema).default({}),
    })
    .strict(),
]);
export type ImportColumnMapping = z.infer<typeof ImportColumnMappingSchema>;

/**
 * Um destino é único dentro do mapeamento INTEIRO, não só dentro da coluna: duas colunas apontando
 * para a mesma tripla somariam sem que ninguém tivesse pedido, e o gestor só descobriria pelo
 * total inflado. A chave do conjunto é (linha, produto, sentido) — o mesmo trio que identifica um
 * contador de máquina.
 */
export function findDuplicateTarget(
  mapping: Record<string, ImportColumnMapping>
): { key: string; columns: string[] } | null {
  const seen = new Map<string, string[]>();
  for (const [column, decision] of Object.entries(mapping)) {
    if (decision.action !== "MAP") continue;
    for (const t of decision.targets) {
      const key = `${t.lineId}|${t.productId}|${t.role}`;
      const columns = seen.get(key) ?? [];
      columns.push(column);
      seen.set(key, columns);
    }
  }
  for (const [key, columns] of seen) {
    if (columns.length > 1) return { key, columns };
  }
  return null;
}

export const ImportMappingSchema = z
  .record(z.string().min(1), ImportColumnMappingSchema)
  .superRefine((mapping, ctx) => {
    const dup = findDuplicateTarget(mapping);
    if (dup) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `As colunas ${dup.columns.join(" e ")} apontam para a mesma linha, produto e sentido. Os valores seriam somados.`,
      });
    }
    const dupChar = findDuplicateCharacteristic(mapping);
    if (dupChar) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `As colunas ${dupChar.columns.join(" e ")} apontam para a mesma característica de batelada. A plataforma não teria como saber qual delas vale.`,
      });
    }
  });
export type ImportMapping = z.infer<typeof ImportMappingSchema>;

// ── Política de conflito ─────────────────────────────────────────────────────────────────────
//
// `KEEP_BOTH` saiu (Spec 082): "manter os dois" não tem significado para um dia de uma linha —
// produziria duas execuções para a mesma data e dobraria o volume no indicador. `FAIL` é o valor
// que faltava: recusar a importação inteira ANTES de escrever qualquer coisa.
export const ConflictPolicySchema = z.enum(["IGNORE", "REPLACE", "FAIL"]);
export type ConflictPolicy = z.infer<typeof ConflictPolicySchema>;

// ── Upload ───────────────────────────────────────────────────────────────────────────────────

export const UploadDataImportRequestSchema = z.object({
  filename: z.string().min(1).max(255),
  lineId: UuidSchema.optional(),
  origin: z.enum(["FILE", "ERP", "MES"]).default("FILE"),
  referenceAt: z.string().datetime().optional(),
});
export type UploadDataImportRequest = z.infer<typeof UploadDataImportRequestSchema>;

// ── Mapeamento ───────────────────────────────────────────────────────────────────────────────

export const MappingDataImportRequestSchema = z.object({
  columnMapping: ImportMappingSchema,
  conflictPolicy: ConflictPolicySchema.default("IGNORE"),
  /** Cabeçalho da coluna que traz o dia. */
  dateColumn: z.string().min(1),
  /** Aba escolhida. Exigido em XLSX, ausente em CSV. */
  sheetName: z.string().min(1).max(120).optional(),
  /** 1-based. A planilha de referência do cliente usa 2 — a linha 1 é um título. */
  headerRow: z.number().int().min(1).default(1),
  /** Nome sob o qual guardar este mapeamento como perfil reutilizável. */
  saveAsProfile: z.string().min(1).max(120).optional(),
  /** Spec 084 — as ligações de origem que valem para o arquivo inteiro. */
  originRules: ImportOriginRulesSchema,
});
export type MappingDataImportRequest = z.infer<typeof MappingDataImportRequestSchema>;

// ── Perfil de mapeamento ─────────────────────────────────────────────────────────────────────

export const ImportProfileRequestSchema = z
  .object({
    name: z.string().min(1).max(120),
    sheetName: z.string().min(1).max(120).nullable().optional(),
    headerRow: z.number().int().min(1).default(1),
    dateColumn: z.string().min(1),
    headerSignature: z.array(z.string()).min(1),
    mapping: ImportMappingSchema,
    originRules: ImportOriginRulesSchema,
  })
  .strict();
export type ImportProfileRequest = z.infer<typeof ImportProfileRequestSchema>;

export const ImportProfileResponseSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  sheetName: z.string().nullable(),
  headerRow: z.number().int(),
  dateColumn: z.string(),
  headerSignature: z.array(z.string()),
  mapping: ImportMappingSchema,
  originRules: z.array(ImportOriginRuleSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ImportProfileResponse = z.infer<typeof ImportProfileResponseSchema>;

// ── Pré-visualização ─────────────────────────────────────────────────────────────────────────

export const ImportPreviewQuerySchema = z.object({
  sheetName: z.string().min(1).max(120).optional(),
  headerRow: z.coerce.number().int().min(1).default(1),
});
export type ImportPreviewQuery = z.infer<typeof ImportPreviewQuerySchema>;

export const TranslatedEntrySchema = z.object({
  column: z.string(),
  lineId: UuidSchema,
  lineName: z.string(),
  productId: UuidSchema,
  productName: z.string(),
  role: ProductionRoleSchema,
  lossType: LossTypeSchema.nullable(),
  rawValue: z.string(),
  /** `null` é AUSÊNCIA de registro, nunca zero. A tela mostra travessão. */
  quantityInLineUnit: z.number().finite().nullable(),
  unit: z.string(),
});
export type TranslatedEntry = z.infer<typeof TranslatedEntrySchema>;

export const RowIssueSchema = z.object({
  column: z.string().nullable(),
  reason: z.string(),
  /** Aviso não impede a linha de ser importada; erro impede. */
  severity: z.enum(["ERROR", "WARNING"]),
});
export type RowIssue = z.infer<typeof RowIssueSchema>;

export const TranslatedRowSchema = z.object({
  /** Número da linha NO ARQUIVO, como o gestor a vê no Excel. */
  line: z.number().int(),
  /** Data civil `YYYY-MM-DD`. Nulo quando a coluna do dia não é uma data. */
  day: z.string().nullable(),
  entries: z.array(TranslatedEntrySchema),
  issues: z.array(RowIssueSchema),
});
export type TranslatedRow = z.infer<typeof TranslatedRowSchema>;

export const ConflictPreviewSchema = z.object({
  day: z.string(),
  lineId: UuidSchema,
  lineName: z.string(),
  /** O que a política escolhida fará com este dia. */
  outcome: z.enum(["SKIP", "REPLACE", "ABORT"]),
  /** Se a execução que já existe veio de importação anterior ou foi digitada na tela. */
  existingSource: z.enum(["import", "manual"]),
});
export type ConflictPreview = z.infer<typeof ConflictPreviewSchema>;

export const ImportPreviewResponseSchema = z.object({
  sheets: z.array(z.string()),
  headers: z.array(z.string()),
  rows: z.array(z.array(z.string())),
  totalSampled: z.number().int(),
  translated: z.array(TranslatedRowSchema),
  ignoredColumns: z.array(z.string()),
  /** Colunas sem decisão. Enquanto houver uma, a validação é recusada. */
  unmappedColumns: z.array(z.string()),
  /**
   * O perfil que casou com estes cabeçalhos, COM o mapeamento inteiro. Devolver só o nome faria a
   * tela ter de buscá-lo de novo para reaplicar (FR-005), e o gestor veria o nome de um perfil que
   * ainda não foi aplicado a nada.
   */
  suggestedProfile: ImportProfileResponseSchema.nullable(),
  conflicts: z.array(ConflictPreviewSchema),
  /** Spec 084 — calculados sobre TODAS as linhas do arquivo, não sobre a amostra. */
  characteristicColumns: z.array(ImportCharacteristicColumnSchema),
  originPreview: z.array(ImportOriginPreviewSchema),
  /**
   * Spec 084 (FR-028) — os dias que o arquivo traria com numerador registrado e base ausente ou
   * zero. É AVISO: não bloqueia a importação, porque o dado pode estar certo e o arquivo é o que a
   * fábrica tem. Dizer antes é o que dá ao gestor a chance de conferir a planilha, não a base.
   */
  suspiciousDays: z.array(
    z.object({
      indicatorId: UuidSchema,
      indicatorName: z.string(),
      absent: z.array(z.string()),
      zero: z.array(z.string()),
    })
  ),
});
export type ImportPreviewResponse = z.infer<typeof ImportPreviewResponseSchema>;

// ── Listagem ─────────────────────────────────────────────────────────────────────────────────

export const ListDataImportsQuerySchema = z.object({
  lineId: UuidSchema.optional(),
  origin: z.enum(["FILE", "ERP", "MES", "MANUAL"]).optional(),
});
export type ListDataImportsQuery = z.infer<typeof ListDataImportsQuerySchema>;
